import React, { useState } from 'react';
import { Button, ContextMenu, ContextMenuItem, ToastUtil } from '@axiom/ui';
import type { Candidate, User } from '@axiom/validation';

import { AxiomForTalentUtil } from '../../utils/axiom-for-talent-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { CopyEmailsUtil } from '../../utils/CopyEmailsUtil';
import { FindOpportunityForCandidateModal } from '../FindOpportunityForCandidateModal/FindOpportunityForCandidateModal';
import type { BioPdfDownloadEntry } from '../BioPdfDownload/BioPdfDownload';
import { BioPdfDownload } from '../BioPdfDownload/BioPdfDownload';

import {
  BULK_DOWNLOAD_BIO_LIMIT,
  BULK_OPEN_BIO_LIMIT,
  BULK_OPEN_TALENT_LIMIT,
  TalentBulkActionsUtil,
} from './TalentBulkActionsUtil';

export type TalentBulkActionsProps = {
  rows: Candidate[];
  checkedCount: number;
  user: User;
  onExportCsv?: () => void | Promise<void>;
};

export const TalentBulkActions = ({
  rows,
  checkedCount,
  user,
  onExportCsv,
}: TalentBulkActionsProps) => {
  const [downloadQueue, setDownloadQueue] = useState<BioPdfDownloadEntry[]>([]);
  const [showFindOppModal, setShowFindOppModal] = useState(false);

  const { eligible, skipped, anonymous } =
    TalentBulkActionsUtil.splitBioRows(rows);

  return (
    <>
      <ContextMenu
        name="BULK_ACTIONS"
        fixed
        direction="right"
        anchor={
          <Button pattern="secondary" name="BULK_ACTIONS_BUTTON">
            BULK ACTIONS
          </Button>
        }
      >
        <ContextMenuItem
          name="BULK_ADD_TO_OPPORTUNITY"
          onClick={() => {
            if (rows.length > 0) {
              setShowFindOppModal(true);
            }
          }}
        >
          Add to opportunity talent pool
        </ContextMenuItem>

        <ContextMenuItem
          name="BULK_COPY_EMAILS"
          onClick={async () => {
            await CopyEmailsUtil.copyEmailsToClipboard(rows);
          }}
        >
          Copy email addresses
        </ContextMenuItem>

        <ContextMenuItem
          name="BULK_OPEN_TALENT"
          disabled={checkedCount > BULK_OPEN_TALENT_LIMIT}
          onClick={() => {
            rows.forEach(row => {
              AxiomForTalentUtil.openTalentDetail(row.id);
            });
          }}
        >
          Open full-page view (limit {BULK_OPEN_TALENT_LIMIT})
        </ContextMenuItem>

        <ContextMenuItem
          name="BULK_OPEN_BIO"
          disabled={checkedCount > BULK_OPEN_BIO_LIMIT}
          onClick={() => {
            eligible.forEach(row => {
              if (CandidateUtil.getBioDisposition(row) === 'named') {
                AxiomForTalentUtil.openBio(row.id);
              } else {
                AxiomForTalentUtil.openAnonymousBio(row.id);
              }
            });

            if (eligible.length > 0 || skipped.length > 0) {
              ToastUtil.add({
                name: 'bulk-open-bios',
                type: 'info',
                dismissible: true,
                children: TalentBulkActionsUtil.buildBulkBioToastChildren({
                  presentParticiple: 'Opening',
                  pastParticiple: 'opened',
                  eligible,
                  skipped,
                  anonymous,
                }),
              });
            }
          }}
        >
          Open bio (limit {BULK_OPEN_BIO_LIMIT})
        </ContextMenuItem>

        <ContextMenuItem
          name="BULK_DOWNLOAD_BIO"
          disabled={checkedCount > BULK_DOWNLOAD_BIO_LIMIT}
          onClick={() => {
            if (eligible.length > 0 || skipped.length > 0) {
              ToastUtil.add({
                name: 'bulk-download-bios',
                type: 'info',
                dismissible: true,
                children: TalentBulkActionsUtil.buildBulkBioToastChildren({
                  presentParticiple: 'Downloading',
                  pastParticiple: 'downloaded',
                  eligible,
                  skipped,
                  anonymous,
                }),
              });
            }

            setDownloadQueue(
              eligible.map(row => ({
                candidateId: row.id,
                isAnonymous:
                  CandidateUtil.getBioDisposition(row) === 'anonymous',
              }))
            );
          }}
        >
          Download bio (limit {BULK_DOWNLOAD_BIO_LIMIT})
        </ContextMenuItem>

        {onExportCsv && !user.roles.includes('EnvoySales') ? (
          <ContextMenuItem
            name="BULK_EXPORT_CSV"
            onClick={() => {
              void onExportCsv();
            }}
          >
            Export to CSV
          </ContextMenuItem>
        ) : null}
      </ContextMenu>

      {showFindOppModal ? (
        <FindOpportunityForCandidateModal
          name="FIND_OPPORTUNITY_FOR_CANDIDATE_MODAL"
          onClose={() => {
            setShowFindOppModal(false);
          }}
          candidateIds={rows.map(row => row.id)}
        />
      ) : null}

      {downloadQueue.length > 0 && (
        <BioPdfDownload
          name="BIO_PDF_DOWNLOAD"
          queue={downloadQueue}
          onComplete={() => {
            setDownloadQueue([]);
          }}
        />
      )}
    </>
  );
};
